import { Controller } from "@hotwired/stimulus"

// Manages adicional (additional product) selection for funerary plans
export default class extends Controller {
  static targets = ["container", "template", "counter", "noResults", "total", "availableGrid", "noAvailable", "search", "searchContainer", "adicionalItem", "noFilterResults"]
  static values = {
    adicionals: { type: Array, default: [] },
    index: { type: Number, default: 0 }
  }

  connect() {
    this.updateTotal()
  }

  filterAdicionals() {
    if (!this.hasSearchTarget) return

    const searchTerm = this.searchTarget.value.toLowerCase().trim()
    let visibleCount = 0

    this.adicionalItemTargets.forEach(item => {
      const name = item.dataset.adicionalSearch || item.dataset.adicionalName?.toLowerCase() || ""

      if (name.includes(searchTerm)) {
        item.classList.remove("hidden")
        visibleCount++
      } else {
        item.classList.add("hidden")
      }
    })

    // Show/hide no filter results message
    if (this.hasNoFilterResultsTarget && this.hasAvailableGridTarget) {
      if (visibleCount === 0 && searchTerm !== "") {
        this.noFilterResultsTarget.classList.remove("hidden")
        this.availableGridTarget.classList.add("hidden")
      } else {
        this.noFilterResultsTarget.classList.add("hidden")
        if (this.adicionalItemTargets.length > 0) {
          this.availableGridTarget.classList.remove("hidden")
        }
      }
    }
  }

  // Update available adicionals when product changes
  updateAvailableAdicionals(adicionals) {
    if (!this.hasAvailableGridTarget) return

    // Clear the grid
    this.availableGridTarget.innerHTML = ""

    // Clear search field
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
    }

    // Hide no filter results
    if (this.hasNoFilterResultsTarget) {
      this.noFilterResultsTarget.classList.add("hidden")
    }

    // Clear selected adicionals (they're from a different product now)
    if (this.hasContainerTarget) {
      // Remove non-persisted items, mark persisted for destruction
      const items = this.containerTarget.querySelectorAll("[data-adicional-item]")
      items.forEach(item => {
        const destroyField = item.querySelector("input[name*='_destroy']")
        if (destroyField) {
          destroyField.value = "1"
          item.classList.add("hidden")
        } else {
          item.remove()
        }
      })
    }

    if (adicionals && adicionals.length > 0) {
      // Populate with new adicionals
      adicionals.forEach(adicional => {
        const button = document.createElement("button")
        button.type = "button"
        button.dataset.adicionalsTarget = "adicionalItem"
        button.dataset.action = "adicionals#add"
        button.dataset.adicionalId = adicional.id
        button.dataset.adicionalName = adicional.name
        button.dataset.adicionalSearch = adicional.name.toLowerCase()
        button.dataset.adicionalPrice = adicional.unit_price
        button.dataset.maxQuantity = adicional.max_quantity || 99
        button.className = "flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors text-left"

        button.innerHTML = `
          <div>
            <p class="font-medium text-gray-900 text-sm">${adicional.name}</p>
            <p class="text-xs text-gray-500 mt-0.5">${adicional.description || ""}</p>
          </div>
          <div class="ml-3 flex-shrink-0 text-right">
            <p class="font-bold text-green-700 text-sm">${this.formatCurrency(adicional.unit_price)}</p>
            <p class="text-xs text-gray-400">máx: ${adicional.max_quantity || "∞"}</p>
          </div>
        `

        this.availableGridTarget.appendChild(button)
      })

      this.availableGridTarget.classList.remove("hidden")
      if (this.hasNoAvailableTarget) {
        this.noAvailableTarget.classList.add("hidden")
      }
      // Show search filter when there are adicionals
      if (this.hasSearchContainerTarget) {
        this.searchContainerTarget.classList.remove("hidden")
      }
    } else {
      this.availableGridTarget.classList.add("hidden")
      if (this.hasNoAvailableTarget) {
        this.noAvailableTarget.classList.remove("hidden")
      }
      // Hide search filter when no adicionals
      if (this.hasSearchContainerTarget) {
        this.searchContainerTarget.classList.add("hidden")
      }
    }

    this.updateTotal()
    this.showNoResultsIfEmpty()
  }

  add(event) {
    event.preventDefault()

    const button = event.currentTarget
    const adicionalId = button.dataset.adicionalId
    const adicionalName = button.dataset.adicionalName
    const adicionalPrice = button.dataset.adicionalPrice
    const maxQuantity = parseInt(button.dataset.maxQuantity) || 99

    // Check if already added (excluding hidden/destroyed items)
    const existingItem = this.containerTarget.querySelector(`[data-adicional-id="${adicionalId}"]:not(.hidden)`)
    if (existingItem) {
      // Increment quantity if not at max
      const quantityInput = existingItem.querySelector("input[name*='quantity']")
      const currentQty = parseInt(quantityInput.value) || 1
      if (currentQty < maxQuantity) {
        quantityInput.value = currentQty + 1
        this.updateItemTotal(existingItem)
        this.updateTotal()
      } else {
        alert(`Quantidade máxima atingida (${maxQuantity})`)
      }
      return
    }

    // Check if there's a hidden item we can restore instead of creating new
    const hiddenItem = this.containerTarget.querySelector(`[data-adicional-id="${adicionalId}"].hidden`)
    if (hiddenItem) {
      // Restore the hidden item
      const destroyField = hiddenItem.querySelector("input[name*='_destroy']")
      if (destroyField) {
        destroyField.value = "0"
      }
      // Reset quantity to 1
      const quantityInput = hiddenItem.querySelector("input[name*='quantity']")
      if (quantityInput) {
        quantityInput.value = 1
      }
      hiddenItem.classList.remove("hidden")
      this.updateItemTotal(hiddenItem)
      this.updateTotal()
      this.hideNoResults()
      return
    }

    // Add new adicional
    // NOTE: For <template> elements, innerHTML may be empty in some browsers.
    // We need to access the content via .content property or use .innerHTML on the template's content
    let templateHTML = this.templateTarget.innerHTML

    // If innerHTML is empty (template element behavior), get content from the template's content fragment
    if (!templateHTML || templateHTML.trim() === '') {
      const templateContent = this.templateTarget.content
      const tempDiv = document.createElement('div')
      tempDiv.appendChild(templateContent.cloneNode(true))
      templateHTML = tempDiv.innerHTML
    }

    const index = this.indexValue
    this.indexValue = index + 1

    const newContent = templateHTML
      .replace(/NEW_INDEX/g, index)
      .replace(/ADICIONAL_ID/g, adicionalId)
      .replace(/ADICIONAL_NAME/g, adicionalName)
      .replace(/ADICIONAL_PRICE/g, adicionalPrice)
      .replace(/MAX_QUANTITY/g, maxQuantity)

    const wrapper = document.createElement("div")
    wrapper.innerHTML = newContent

    const newElement = wrapper.firstElementChild

    // Format the unit price display
    const unitPriceDisplay = newElement.querySelector("[data-unit-price-display]")
    if (unitPriceDisplay) {
      unitPriceDisplay.textContent = this.formatCurrency(parseFloat(adicionalPrice))
    }

    // Format the item total display
    const itemTotalDisplay = newElement.querySelector("[data-item-total]")
    if (itemTotalDisplay) {
      itemTotalDisplay.textContent = this.formatCurrency(parseFloat(adicionalPrice))
    }

    this.containerTarget.appendChild(newElement)

    this.updateTotal()
    this.hideNoResults()
  }

  remove(event) {
    event.preventDefault()
    const item = event.target.closest("[data-adicional-item]")
    if (item) {
      // Add slide-out animation
      item.style.opacity = "0"
      item.style.transform = "translateX(20px)"

      setTimeout(() => {
        // If it has an id field (existing record), mark for destruction
        const destroyField = item.querySelector("input[name*='_destroy']")
        if (destroyField) {
          destroyField.value = "1"
          item.classList.add("hidden")
          item.style.opacity = ""
          item.style.transform = ""
        } else {
          item.remove()
        }
        this.updateTotal()
        this.showNoResultsIfEmpty()
      }, 200)
    }
  }

  updateQuantity(event) {
    const item = event.target.closest("[data-adicional-item]")

    // Just update totals on input, don't clamp yet
    this.updateItemTotal(item)
    this.updateTotal()
    this.updateButtonStates(item)
    this.updateQuantityDisplay(item)
  }

  increment(event) {
    event.preventDefault()
    const item = event.target.closest("[data-adicional-item]")
    const quantityInput = item.querySelector("input[name*='quantity']")
    const maxQuantity = parseInt(quantityInput.dataset.maxQuantity) || 99
    let value = parseInt(quantityInput.value) || 1

    if (value < maxQuantity) {
      quantityInput.value = value + 1
      this.updateItemTotal(item)
      this.updateTotal()
      this.updateButtonStates(item)
      this.updateQuantityDisplay(item)
    }
  }

  decrement(event) {
    event.preventDefault()
    const item = event.target.closest("[data-adicional-item]")
    const quantityInput = item.querySelector("input[name*='quantity']")
    let value = parseInt(quantityInput.value) || 1

    if (value > 1) {
      quantityInput.value = value - 1
      this.updateItemTotal(item)
      this.updateTotal()
      this.updateButtonStates(item)
      this.updateQuantityDisplay(item)
    }
  }

  updateButtonStates(item) {
    const quantityInput = item.querySelector("input[name*='quantity']")
    const maxQuantity = parseInt(quantityInput.dataset.maxQuantity) || 99
    const value = parseInt(quantityInput.value) || 1

    const decrementBtn = item.querySelector("[data-action*='decrement']")
    const incrementBtn = item.querySelector("[data-action*='increment']")

    if (decrementBtn) {
      decrementBtn.disabled = value <= 1
    }
    if (incrementBtn) {
      incrementBtn.disabled = value >= maxQuantity
    }
  }

  updateQuantityDisplay(item) {
    const quantityInput = item.querySelector("input[name*='quantity']")
    const maxQuantity = parseInt(item.dataset.maxQuantity) || parseInt(quantityInput.dataset.maxQuantity) || 99
    const value = parseInt(quantityInput.value) || 1

    const display = item.querySelector("[data-quantity-display]")
    if (display) {
      display.textContent = `${value}/${maxQuantity}`
    }
  }

  // Clamp value only when field loses focus
  clampQuantity(event) {
    const maxQuantity = parseInt(event.target.dataset.maxQuantity) || 99
    let value = parseInt(event.target.value) || 1

    // Clamp value
    if (value < 1) value = 1
    if (value > maxQuantity) value = maxQuantity
    event.target.value = value

    const item = event.target.closest("[data-adicional-item]")
    this.updateItemTotal(item)
    this.updateTotal()
    this.updateButtonStates(item)
    this.updateQuantityDisplay(item)
  }

  updateItemTotal(item) {
    const quantityInput = item.querySelector("input[name*='quantity']")
    const unitPrice = parseFloat(item.dataset.unitPrice) || 0
    const quantity = parseInt(quantityInput.value) || 1
    const total = unitPrice * quantity

    const totalDisplay = item.querySelector("[data-item-total]")
    if (totalDisplay) {
      totalDisplay.textContent = this.formatCurrency(total)
    }
  }

  updateTotal() {
    if (!this.hasTotalTarget) return

    const items = this.containerTarget.querySelectorAll("[data-adicional-item]:not(.hidden)")
    let total = 0

    items.forEach(item => {
      const quantityInput = item.querySelector("input[name*='quantity']")
      const unitPrice = parseFloat(item.dataset.unitPrice) || 0
      const quantity = parseInt(quantityInput?.value) || 1
      total += unitPrice * quantity
    })

    this.totalTarget.textContent = this.formatCurrency(total)

    // Update counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${items.length} item(ns) selecionado(s)`
    }

    // Dispatch event to notify payment_fields_controller to recalculate
    this.element.dispatchEvent(new CustomEvent("adicionals:changed", {
      bubbles: true,
      detail: { total }
    }))
  }

  hideNoResults() {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add("hidden")
    }
  }

  showNoResultsIfEmpty() {
    if (!this.hasNoResultsTarget) return

    const items = this.containerTarget.querySelectorAll("[data-adicional-item]:not(.hidden)")
    if (items.length === 0) {
      this.noResultsTarget.classList.remove("hidden")
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value)
  }
}
